'use client';

import { deleteCookie, setCookie } from 'cookies-next';
import { createContext, ReactNode, useContext, useState } from 'react';

export type TriggeredByType =
  | 'automaticTrigger'
  | 'init'
  | 'searchButton'
  | 'calendarSlider'
  | 'refetchAutomaticRoomDistribution'
  | 'filtering';

export interface CookieValues {
  adultsNumber?: string;
  adultsPerRoom?: string;
  bonusCodes?: string;
  childrenAges?: string;
  childrenAgesPerRoom?: string;
  departureDate?: string;
  departureIds?: string;
  periodType?: string;
}

interface SearchContextType {
  activeSection: number;
  adultsNo: string;
  adultsNoPerRoom: string;
  area: string;
  automaticRoomAssignment: boolean;
  bonusCodes: string;
  brand: Content.Properties.Brand;
  campaignAvailableDates: Date[];
  childrenAges: string;
  childrenAgesPerRoom: string;
  countryIds: string;
  departureDate: string;
  departureIds: string;
  destinationIds: string;
  destinationRegionIds: string;
  hotelIds: string;
  isClickedEditSearch: boolean;
  maxDepartureDate: string;
  periodType: string;
  setActiveSection: (activeSection: number) => void;
  setAdultsNo: (adults: string) => void;
  setAdultsNoPerRoom: (adultsPerRoom: string) => void;
  setArea: (area: string) => void;
  setAutomaticRoomAssignment: (automaticRoomAssignment: boolean) => void;
  setBonusCodes: (codes: string) => void;
  setBrand: (value: Content.Properties.Brand) => void;
  setCampaignAvailableDates: (value: Date[]) => void;
  setChildrenAges: (ages: string) => void;
  setChildrenAgesPerRoom: (agesPerRoom: string) => void;
  setCountryIds: (ids: string) => void;
  setDepartureDate: (date: string) => void;
  setDepartureIds: (ids: string) => void;
  setDestinationIds: (ids: string) => void;
  setDestinationRegionIds: (ids: string) => void;
  setHotelIds: (ids: string) => void;
  setIsClickedEditSearch: (value: boolean) => void;
  setMaxDepartureDate: (date: string) => void;
  setPeriodType: (type: string) => void;
  setTriggeredBy: (trigger: TriggeredByType) => void;
  triggeredBy: TriggeredByType;
  updateCookies: (values: CookieValues) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchContextProvider = ({
  children,
  initialData,
}: {
  children: ReactNode;
  initialData?: {
    area: string;
    brand: Content.Properties.Brand;
    searchContext?: CookieValues;
  };
}) => {
  const [activeSection, setActiveSection] = useState(-1);
  const [adultsNo, setAdultsNo] = useState(
    initialData?.searchContext?.adultsNumber
      ? decodeURIComponent(initialData.searchContext.adultsNumber)
      : '',
  );
  const [area, setArea] = useState<string>(
    initialData?.area ? initialData.area : '',
  );
  const [brand, setBrand] = useState<Content.Properties.Brand>(
    initialData?.brand ? initialData.brand : 'default',
  );
  const [bonusCodes, setBonusCodes] = useState(
    initialData?.searchContext?.bonusCodes
      ? initialData.searchContext.bonusCodes
      : '',
  );
  const [adultsNoPerRoom, setAdultsNoPerRoom] = useState(
    initialData?.searchContext?.adultsPerRoom
      ? decodeURIComponent(initialData.searchContext.adultsPerRoom)
      : '',
  );
  const [automaticRoomAssignment, setAutomaticRoomAssignment] = useState(true);
  const [campaignAvailableDates, setCampaignAvailableDates] = useState<Date[]>(
    [],
  );
  const [childrenAges, setChildrenAges] = useState(
    initialData?.searchContext?.childrenAges
      ? decodeURIComponent(initialData.searchContext.childrenAges)
      : '',
  );
  const [childrenAgesPerRoom, setChildrenAgesPerRoom] = useState(
    initialData?.searchContext?.childrenAgesPerRoom
      ? decodeURIComponent(initialData.searchContext.childrenAgesPerRoom)
      : '',
  );
  const [departureDate, setDepartureDate] = useState(
    initialData?.searchContext?.departureDate
      ? decodeURIComponent(initialData.searchContext.departureDate)
      : '',
  );
  const [maxDepartureDate, setMaxDepartureDate] = useState('');
  const [departureIds, setDepartureIds] = useState(
    initialData?.searchContext?.departureIds
      ? decodeURIComponent(initialData.searchContext.departureIds)
      : '',
  );
  const [countryIds, setCountryIds] = useState('');
  const [destinationIds, setDestinationIds] = useState('');
  const [destinationRegionIds, setDestinationRegionIds] = useState('');
  const [hotelIds, setHotelIds] = useState('');
  const [isClickedEditSearch, setIsClickedEditSearch] = useState(false);
  const [periodType, setPeriodType] = useState(
    initialData?.searchContext?.periodType
      ? decodeURIComponent(initialData.searchContext.periodType)
      : '',
  );
  const [triggeredBy, setTriggeredBy] = useState<TriggeredByType>('init');

  const updateCookies = (values: CookieValues) => {
    for (const [key, value] of Object.entries(values)) {
      if (value) {
        setCookie(`search.${key}`, String(value));
      } else {
        deleteCookie(`search.${key}`);
      }
    }
  };

  return (
    <SearchContext.Provider
      value={{
        activeSection,
        adultsNo,
        adultsNoPerRoom,
        area,
        automaticRoomAssignment,
        bonusCodes,
        brand,
        campaignAvailableDates,
        childrenAges,
        childrenAgesPerRoom,
        countryIds,
        departureDate,
        departureIds,
        destinationIds,
        destinationRegionIds,
        hotelIds,
        isClickedEditSearch,
        maxDepartureDate,
        periodType,
        setActiveSection,
        setAdultsNo,
        setAdultsNoPerRoom,
        setArea,
        setAutomaticRoomAssignment,
        setBonusCodes,
        setBrand,
        setCampaignAvailableDates,
        setChildrenAges,
        setChildrenAgesPerRoom,
        setCountryIds,
        setDepartureDate,
        setDepartureIds,
        setDestinationIds,
        setDestinationRegionIds,
        setHotelIds,
        setIsClickedEditSearch,
        setMaxDepartureDate,
        setPeriodType,
        setTriggeredBy,
        triggeredBy,
        updateCookies,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => {
  const context = useContext(SearchContext);

  if (!context) {
    throw new Error(
      'useSearchContext must be used within a SearchContextProvider',
    );
  }

  return context;
};
